import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128, pattern: "^\\S+$" });
const PositiveMoneyFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const PositiveQuantitySchema = Type.Integer({ minimum: 1, maximum: 999_999 });
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern:
    "^[0-9]{4}-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])T([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]\\.[0-9]{3}Z$",
});
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const NoteSchema = Type.String({
  minLength: 2,
  maxLength: 300,
  pattern: "^\\S(?:[\\s\\S]*\\S)?$",
});

export const CustomerRentalStatusSchema = Type.Union([
  Type.Literal("ACTIVE"),
  Type.Literal("PARTIALLY_RETURNED"),
  Type.Literal("RETURNED"),
  Type.Literal("EXCEPTION_PENDING"),
  Type.Literal("EXCEPTION_CLOSED"),
]);
export type CustomerRentalStatus = Static<typeof CustomerRentalStatusSchema>;

export const CustomerRentalExceptionTypeSchema = Type.Union([
  Type.Literal("LOST"),
  Type.Literal("DAMAGED"),
  Type.Literal("OVERDUE"),
]);
export type CustomerRentalExceptionType = Static<typeof CustomerRentalExceptionTypeSchema>;

export const CustomerRentalHistoryActionSchema = Type.Union([
  Type.Literal("RENTED"),
  Type.Literal("RETURNED"),
  Type.Literal("EXCEPTION_REPORTED"),
  Type.Literal("EXCEPTION_CONFIRMED"),
  Type.Literal("EXCEPTION_REJECTED"),
]);
export type CustomerRentalHistoryAction = Static<typeof CustomerRentalHistoryActionSchema>;

export const CustomerRentalChargeItemSchema = Type.Object(
  {
    id: StableIdSchema,
    rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
    orderId: StableIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
    sessionId: StableIdSchema,
    businessDate: BusinessDateSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    skuId: StableIdSchema,
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    productName: Type.String({ minLength: 1, maxLength: 120 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    unitRentalPriceFen: PositiveMoneyFenSchema,
    quantity: PositiveQuantitySchema,
    receivableFen: PositiveMoneyFenSchema,
    createdAt: CanonicalDateTimeSchema,
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type CustomerRentalChargeItem = Static<typeof CustomerRentalChargeItemSchema>;

export const CustomerRentalHistoryEntrySchema = Type.Object(
  {
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    action: CustomerRentalHistoryActionSchema,
    quantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
    remainingQuantityAfter: Type.Integer({ minimum: 0, maximum: 999_999 }),
    note: NoteSchema,
    operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    occurredAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerRentalHistoryEntry = Static<typeof CustomerRentalHistoryEntrySchema>;

export const CustomerRentalExceptionSnapshotSchema = Type.Object(
  {
    exceptionType: CustomerRentalExceptionTypeSchema,
    note: NoteSchema,
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("CONFIRMED"),
      Type.Literal("REJECTED"),
    ]),
    workItemId: StableIdSchema,
    reportedByActorId: StableIdSchema,
    reportedAt: CanonicalDateTimeSchema,
    resolvedAt: Type.Union([CanonicalDateTimeSchema, Type.Null()]),
    resolutionReason: Type.Union([NoteSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CustomerRentalExceptionSnapshot = Static<typeof CustomerRentalExceptionSnapshotSchema>;

export const CustomerRentalRecordSchema = Type.Object(
  {
    rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    chargeItem: CustomerRentalChargeItemSchema,
    originalQuantity: PositiveQuantitySchema,
    remainingQuantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
    status: CustomerRentalStatusSchema,
    overdue: Type.Boolean(),
    expectedReturnAt: CanonicalDateTimeSchema,
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
    latestException: Type.Union([CustomerRentalExceptionSnapshotSchema, Type.Null()]),
    history: Type.Array(CustomerRentalHistoryEntrySchema, { minItems: 1, maxItems: 10_000 }),
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type CustomerRentalRecord = Static<typeof CustomerRentalRecordSchema>;

export const CustomerRentalCandidateSchema = Type.Object(
  {
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
    orderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    businessDate: BusinessDateSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    skuId: StableIdSchema,
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    productName: Type.String({ minLength: 1, maxLength: 120 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    unitRentalPriceFen: PositiveMoneyFenSchema,
    configuredQuantity: PositiveQuantitySchema,
    unavailableQuantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
    availableQuantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
  },
  { additionalProperties: false },
);
export type CustomerRentalCandidate = Static<typeof CustomerRentalCandidateSchema>;
export const CustomerRentalCandidateListSchema = Type.Array(CustomerRentalCandidateSchema, {
  maxItems: 10_000,
});

export const CustomerRentalCreateRequestSchema = Type.Object(
  {
    sessionId: StableIdSchema,
    skuId: StableIdSchema,
    quantity: PositiveQuantitySchema,
    expectedReturnAt: CanonicalDateTimeSchema,
    note: NoteSchema,
    expectedOrderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedProductVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerRentalCreateRequest = Static<typeof CustomerRentalCreateRequestSchema>;

export const CustomerRentalReturnRequestSchema = Type.Object(
  {
    quantity: PositiveQuantitySchema,
    note: NoteSchema,
    expectedVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerRentalReturnRequest = Static<typeof CustomerRentalReturnRequestSchema>;

export const CustomerRentalExceptionReportRequestSchema = Type.Object(
  {
    exceptionType: CustomerRentalExceptionTypeSchema,
    note: NoteSchema,
    expectedVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerRentalExceptionReportRequest = Static<
  typeof CustomerRentalExceptionReportRequestSchema
>;

export const CustomerRentalExceptionDecisionRequestSchema = Type.Object(
  {
    decision: Type.Union([Type.Literal("CONFIRM"), Type.Literal("REJECT")]),
    reason: NoteSchema,
    expectedRentalVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedWorkItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerRentalExceptionDecisionRequest = Static<
  typeof CustomerRentalExceptionDecisionRequestSchema
>;

export const CustomerRentalOperationTypeSchema = Type.Union([
  Type.Literal("CREATE"),
  Type.Literal("RETURN"),
  Type.Literal("REPORT_EXCEPTION"),
  Type.Literal("DECIDE_EXCEPTION"),
]);
export type CustomerRentalOperationType = Static<typeof CustomerRentalOperationTypeSchema>;

export const CustomerRentalOperationBindingSchema = Type.Union([
  Type.Object(
    {
      operationType: Type.Literal("CREATE"),
      request: CustomerRentalCreateRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("RETURN"),
      rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
      request: CustomerRentalReturnRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("REPORT_EXCEPTION"),
      rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
      request: CustomerRentalExceptionReportRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("DECIDE_EXCEPTION"),
      rentalNumber: Type.String({ pattern: "^ZL-[0-9]{8}-[0-9A-F]{12}$" }),
      workItemId: StableIdSchema,
      request: CustomerRentalExceptionDecisionRequestSchema,
    },
    { additionalProperties: false },
  ),
]);
export type CustomerRentalOperationBinding = Static<typeof CustomerRentalOperationBindingSchema>;

export const CustomerRentalOperationResultSchema = Type.Object(
  {
    rental: CustomerRentalRecordSchema,
    workItemId: Type.Union([StableIdSchema, Type.Null()]),
    operation: CustomerRentalOperationBindingSchema,
  },
  { additionalProperties: false },
);
export type CustomerRentalOperationResult = Static<typeof CustomerRentalOperationResultSchema>;

function isCanonicalTimestamp(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function isCanonicalNote(value: string): boolean {
  return value.trim() === value;
}

export function isCustomerRentalCandidateList(value: unknown): value is CustomerRentalCandidate[] {
  if (!Value.Check(CustomerRentalCandidateListSchema, value)) return false;
  const keys = new Set<string>();
  return (value as CustomerRentalCandidate[]).every((candidate) => {
    const key = `${candidate.sessionId}:${candidate.skuId}`;
    if (
      keys.has(key) ||
      candidate.availableQuantity !==
        Math.max(0, candidate.configuredQuantity - candidate.unavailableQuantity)
    ) {
      return false;
    }
    keys.add(key);
    return true;
  });
}

export function isCustomerRentalCreateRequest(
  value: unknown,
): value is CustomerRentalCreateRequest {
  if (!Value.Check(CustomerRentalCreateRequestSchema, value)) return false;
  const request = value as CustomerRentalCreateRequest;
  return isCanonicalTimestamp(request.expectedReturnAt) && isCanonicalNote(request.note);
}

export function isCustomerRentalReturnRequest(
  value: unknown,
): value is CustomerRentalReturnRequest {
  return (
    Value.Check(CustomerRentalReturnRequestSchema, value) &&
    isCanonicalNote((value as CustomerRentalReturnRequest).note)
  );
}

export function isCustomerRentalExceptionReportRequest(
  value: unknown,
): value is CustomerRentalExceptionReportRequest {
  return (
    Value.Check(CustomerRentalExceptionReportRequestSchema, value) &&
    isCanonicalNote((value as CustomerRentalExceptionReportRequest).note)
  );
}

export function isCustomerRentalExceptionDecisionRequest(
  value: unknown,
): value is CustomerRentalExceptionDecisionRequest {
  return (
    Value.Check(CustomerRentalExceptionDecisionRequestSchema, value) &&
    isCanonicalNote((value as CustomerRentalExceptionDecisionRequest).reason)
  );
}

export function isCustomerRentalChargeItem(value: unknown): value is CustomerRentalChargeItem {
  if (!Value.Check(CustomerRentalChargeItemSchema, value)) return false;
  const item = value as CustomerRentalChargeItem;
  return (
    isCanonicalTimestamp(item.createdAt) &&
    Number.isSafeInteger(item.unitRentalPriceFen * item.quantity) &&
    item.receivableFen === item.unitRentalPriceFen * item.quantity
  );
}

export function isCustomerRentalRecord(value: unknown): value is CustomerRentalRecord {
  if (!Value.Check(CustomerRentalRecordSchema, value)) return false;
  const record = value as CustomerRentalRecord;
  if (
    !isCustomerRentalChargeItem(record.chargeItem) ||
    record.rentalNumber !== record.chargeItem.rentalNumber ||
    record.originalQuantity !== record.chargeItem.quantity ||
    record.remainingQuantity > record.originalQuantity ||
    record.history.length !== record.version ||
    !isCanonicalTimestamp(record.expectedReturnAt) ||
    !isCanonicalTimestamp(record.createdAt) ||
    !isCanonicalTimestamp(record.updatedAt)
  ) {
    return false;
  }
  let remaining = record.originalQuantity;
  for (const [index, entry] of record.history.entries()) {
    if (
      entry.version !== index + 1 ||
      !isCanonicalTimestamp(entry.occurredAt) ||
      !isCanonicalNote(entry.note) ||
      entry.operatorDisplayName.trim() !== entry.operatorDisplayName
    ) {
      return false;
    }
    if (index === 0) {
      if (
        entry.action !== "RENTED" ||
        entry.quantity !== record.originalQuantity ||
        entry.remainingQuantityAfter !== record.originalQuantity
      ) {
        return false;
      }
    } else if (entry.action === "RETURNED") {
      if (entry.quantity < 1 || entry.quantity > remaining) return false;
      remaining -= entry.quantity;
      if (entry.remainingQuantityAfter !== remaining) return false;
    } else if (entry.quantity !== 0 || entry.remainingQuantityAfter !== remaining) {
      return false;
    }
  }
  if (remaining !== record.remainingQuantity) return false;
  const operationalStatus =
    remaining === 0
      ? "RETURNED"
      : remaining === record.originalQuantity
        ? "ACTIVE"
        : "PARTIALLY_RETURNED";
  if (
    (record.status === "RETURNED" && remaining !== 0) ||
    ((record.status === "ACTIVE" || record.status === "PARTIALLY_RETURNED") &&
      record.status !== operationalStatus) ||
    (record.status === "EXCEPTION_PENDING" && record.latestException?.status !== "PENDING") ||
    (record.status === "EXCEPTION_CLOSED" &&
      (remaining === 0 ||
        record.latestException?.status !== "CONFIRMED" ||
        record.latestException.exceptionType === "OVERDUE"))
  ) {
    return false;
  }
  if (record.latestException) {
    if (
      !isCanonicalTimestamp(record.latestException.reportedAt) ||
      (record.latestException.status === "PENDING") !==
        (record.latestException.resolvedAt === null &&
          record.latestException.resolutionReason === null) ||
      (record.latestException.status !== "PENDING" &&
        (record.latestException.resolvedAt === null ||
          record.latestException.resolutionReason === null))
    ) {
      return false;
    }
  }
  return true;
}

export function isCustomerRentalOperationResult(
  value: unknown,
): value is CustomerRentalOperationResult {
  if (!Value.Check(CustomerRentalOperationResultSchema, value)) return false;
  const result = value as CustomerRentalOperationResult;
  if (!isCustomerRentalRecord(result.rental)) return false;
  switch (result.operation.operationType) {
    case "CREATE":
      return result.workItemId === null;
    case "RETURN":
      return (
        result.workItemId === null && result.operation.rentalNumber === result.rental.rentalNumber
      );
    case "REPORT_EXCEPTION":
      return (
        result.workItemId !== null &&
        result.operation.rentalNumber === result.rental.rentalNumber &&
        result.rental.latestException?.workItemId === result.workItemId
      );
    case "DECIDE_EXCEPTION":
      return (
        result.workItemId === result.operation.workItemId &&
        result.operation.rentalNumber === result.rental.rentalNumber &&
        result.rental.latestException?.workItemId === result.workItemId
      );
  }
}
